import { useEffect, useState, createContext, useContext, ReactNode, useCallback, useRef } from 'react';

interface ToastMessage {
  id: number;
  type: 'success' | 'error' | 'info';
  message: string;
}

interface ToastContextType {
  addToast: (type: ToastMessage['type'], message: string) => void;
}

const ToastContext = createContext<ToastContextType>({ addToast: () => {} });

let nextId = 0;

/** Errors stay until dismissed — they usually carry something to act on. */
const DURATION: Record<ToastMessage['type'], number | null> = {
  success: 4000,
  info: 4000,
  error: null,
};

const MAX_VISIBLE = 3;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const addToast = useCallback((type: ToastMessage['type'], message: string) => {
    setToasts(prev => [...prev, { id: nextId++, type, message }].slice(-MAX_VISIBLE));
  }, []);

  return (
    <ToastContext.Provider value={{ addToast }}>
      {children}
      {/* Fills the width on a phone: the old min-w-[300px] plus right-4 needed
          332px and scrolled a 320px viewport sideways. Sits above the bottom
          bar rather than over it, so a toast never buries the tab bar. */}
      <div className="fixed bottom-[calc(var(--pc-bottom-bar-h)_+_1rem)] inset-x-4 sm:inset-x-auto sm:right-4 z-50 flex flex-col gap-2 sm:items-end pointer-events-none">
        {toasts.map(t => (
          <ToastItem key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastItem({ toast, onDismiss }: { toast: ToastMessage; onDismiss: () => void }) {
  const [paused, setPaused] = useState(false);
  const dismissRef = useRef(onDismiss);
  dismissRef.current = onDismiss;

  useEffect(() => {
    const ms = DURATION[toast.type];
    if (ms === null || paused) return;
    const timer = setTimeout(() => dismissRef.current(), ms);
    return () => clearTimeout(timer);
  }, [toast.type, paused]);

  const colors = {
    success: 'bg-status-up text-white',
    error: 'bg-status-down text-white',
    info: 'bg-brand-600 text-white',
  };

  return (
    <div
      // role="alert" is assertive; role="status" is polite. Previously neither
      // was set, so no toast was ever announced.
      role={toast.type === 'error' ? 'alert' : 'status'}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      className={`${colors[toast.type]} pointer-events-auto animate-slide-in px-4 py-3 rounded-lg shadow-overlay flex items-start gap-3 w-full sm:w-auto sm:min-w-[20rem] sm:max-w-md`}
    >
      <span className="flex-1 text-sm">{toast.message}</span>
      <button
        type="button"
        onClick={onDismiss}
        className="shrink-0 text-white/80 hover:text-white rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
        aria-label="Dismiss"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

export const useToast = () => useContext(ToastContext);
